import React, { useState } from "react";
import styled from "styled-components";
import { Layout } from "../components";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { experienceData } from "../data/experienceData";

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  margin-bottom: 20px;
  padding: 60px 20px;
  min-height: 100vh;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  padding-bottom: 60px;
`;

const ProjectCard = styled.div`
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid hsl(180, 7%, 57%);

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    border-color: hsl(180, 31%, 82%);
  }
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 200px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(45deg, hsl(173, 14%, 12%), hsl(173, 12%, 27%));
  display: flex;
  align-items: center;
  justify-content: center;
  color: hsl(180, 31%, 82%);
  font-size: 2rem;
  font-weight: bold;

  .image-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 2;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: top left;
    }
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    z-index: 1;
  }
`;

const ProjectContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 25px;
`;

const ProjectTitle = styled.a`
  font-size: 2.4rem;
  margin-bottom: 15px;
  color: hsl(132, 4%, 25%);

  &:hover {
    color: hsl(349, 100%, 40%);
    text-decoration: underline;
  }
`;

const ProjectMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  font-size: 1.4rem;
  color: hsl(132, 6%, 40%);
`;

const TechBadge = styled.span`
  background: linear-gradient(
    135deg,
    hsl(173, 14%, 12%) 0%,
    hsl(173, 12%, 27%) 100%
  );
  color: hsl(180, 31%, 82%);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 1.2rem;
  font-weight: 500;
`;

const ProjectDescription = styled.p`
  flex-grow: 1;
  font-size: 1.5rem;
  line-height: 1.6;
  color: hsl(132, 6%, 30%);
  margin-bottom: 20px;
`;

const CompanyLink = styled.a`
  font-size: 1.4rem;
  color: hsl(349, 100%, 33%);
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    color: hsl(349, 100%, 40%);
    text-decoration: underline;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 2px solid hsl(173, 14%, 12%);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 20px;
  padding: 0px 7px;
  background: rgba(96, 108, 98, 0.8);
  border: none;
  font-size: 3rem;
  cursor: pointer;
  color: hsl(131, 8.9%, 62.5%);
  z-index: 1001;

  &:hover {
    color: hsl(173, 14%, 12%);
  }

  border-radius: 5px;
  transition: background 0.3s ease;
`;

const ModalHeader = styled.div`
  padding: 30px 30px 20px;
  border-bottom: 1px solid #eee;
  background: linear-gradient(
    135deg,
    hsl(173, 14%, 12%) 0%,
    hsl(173, 12%, 27%) 100%
  );
  color: hsl(180, 31%, 82%);

  ${ProjectTitle} {
    color: hsl(180, 31%, 82%);
  }

  ${ProjectMeta} {
    color: hsl(180, 7%, 57%);
  }

  ${CompanyLink} {
    color: hsl(180, 31%, 82%);

    &:hover {
      color: white;
    }
  }
`;

const ModalBody = styled.div`
  padding: 30px;
`;

const TechList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const TechItem = styled.span`
  background: hsl(173, 12%, 27%);
  color: hsl(180, 31%, 82%);
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 1.3rem;
`;

const ResponsibilitiesList = styled.ul`
  list-style: none;
  margin-top: 20px;
`;

const AccomplishmentItem = styled.li`
  font-size: 1.4rem;
  line-height: 1.6;
  color: hsl(132, 6%, 30%);
  margin-bottom: 10px;
  padding-left: 20px;
  position: relative;

  &::before {
    content: "✓";
    position: absolute;
    left: 0;
    color: hsl(173, 14%, 12%);
    font-weight: bold;
  }
`;

const ModalSectionTitle = styled.h4`
  font-size: 1.8rem;
  margin-bottom: 15px;
  color: hsl(173, 14%, 12%);
`;

const projects = experienceData.reduce((acc, company) => {
  const { projects, ...companyData } = company;
  const projectsWithCompanyData = company.projects.map((project) => ({
    ...companyData,
    ...project,
  }));

  return [...acc, ...projectsWithCompanyData];
}, []);

const projectsImages = {
  "Passion.io": require(`../assets/images/Passion.io.png`),
  "appt-now": require(`../assets/images/appt-now.png`),
  Diggity: require(`../assets/images/Diggity.png`),
  Mittspor: require(`../assets/images/Mittspor.png`),
  Omnistools: require(`../assets/images/Omnistools.png`),
  Chekin: require(`../assets/images/Chekin.png`),
  Sharekey: require(`../assets/images/Sharekey.png`),
  Emirates: require(`../assets/images/Emirates.png`),
  "Refinitiv Eikon": require(`../assets/images/Refinitiv Eikon.png`),
};

const ProjectTitleLink = ({ project }) => (
  <div onClick={(e) => e.stopPropagation()}>
    <ProjectTitle
      href={project.productLink}
      target="_blank"
      rel="noopener noreferrer"
    >
      {project.projectName}
    </ProjectTitle>
  </div>
);

export const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  const openModal = (project) => {
    setSelectedProject(project);
  };

  const closeModal = () => {
    setSelectedProject(null);
  };

  return (
    <Layout>
      <ContentWrapper>
        <ProjectsGrid>
          {projects.map((project) => (
            <ProjectCard key={project.id} onClick={() => openModal(project)}>
              <ProjectImage>
                <div className="image-wrapper">
                  <LazyLoadImage
                    src={projectsImages[project.projectName]}
                    alt={project.projectDescription}
                    effect="blur"
                    width="100%"
                    height="100%"
                  />
                </div>
              </ProjectImage>
              <ProjectContent>
                <ProjectTitleLink project={project} />
                <ProjectMeta>
                  <span>{project.period}</span>
                  <TechBadge>{project.mainTech}</TechBadge>
                </ProjectMeta>
                <ProjectDescription>
                  {project.projectDescription}
                </ProjectDescription>
                <CompanyLink
                  href={project.companyLink}
                  onClick={(e) => e.stopPropagation()}
                >
                  {project.company}
                </CompanyLink>
              </ProjectContent>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </ContentWrapper>

      {selectedProject && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeModal}>&times;</CloseButton>

            <ProjectImage>
              <div className="image-wrapper">
                <LazyLoadImage
                  src={projectsImages[selectedProject.projectName]}
                  alt={selectedProject.projectDescription}
                  effect="blur"
                  width="100%"
                  height="100%"
                  objectFit="fill"
                />
              </div>
            </ProjectImage>

            <ModalHeader>
              <ProjectTitleLink project={selectedProject} />
              <ProjectMeta>
                <span>
                  <strong>Role:</strong> {selectedProject.projectRole}
                </span>
                <span>
                  <strong>Period:</strong> {selectedProject.period}
                </span>
              </ProjectMeta>
              <CompanyLink href={selectedProject.companyLink}>
                {selectedProject.company} ({selectedProject.companyType})
              </CompanyLink>
            </ModalHeader>

            <ModalBody>
              <ProjectDescription>
                {selectedProject.projectDescription}
              </ProjectDescription>

              <ModalSectionTitle>Technologies Used:</ModalSectionTitle>
              <TechList>
                {selectedProject.technologies.map((tech, index) => (
                  <TechItem key={index}>{tech}</TechItem>
                ))}
              </TechList>

              {selectedProject.achievements && (
                <>
                  <ModalSectionTitle>Achievements:</ModalSectionTitle>
                  <ResponsibilitiesList>
                    {selectedProject.achievements.map(
                      (achievement, index) => (
                        <AccomplishmentItem key={index}>
                          {achievement}
                        </AccomplishmentItem>
                      )
                    )}
                  </ResponsibilitiesList>
                </>
              )}

              <ModalSectionTitle>Key responsibilities:</ModalSectionTitle>
              <ResponsibilitiesList>
                {selectedProject.responsibilities.map(
                  (responsibility, index) => (
                    <AccomplishmentItem key={index}>
                      {responsibility}
                    </AccomplishmentItem>
                  )
                )}
              </ResponsibilitiesList>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Layout>
  );
};
